import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, Package, Percent, Hash, IndianRupee } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { LoadingButton } from '../components/ui/LoadingButton'
import { Input } from '../components/ui/Input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { apiClient } from '../services/api'
import toast from 'react-hot-toast'

interface Category {
  id: string
  name: string
  description: string
}

interface Unit {
  id: string
  name: string
  short_name: string
  description: string | null
}

interface ProductFormData {
  name: string
  description: string
  sku: string
  category_id: string
  price: string
  mrp: string
  cost_price: string
  stock_quantity: string
  min_stock_level: string
  max_stock_level: string
  unit: string
  gst_rate: string
  gst_type: 'inclusive' | 'exclusive'
  hsn_code: string
  is_active: boolean
}

export const ProductForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditMode = Boolean(id)

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [categories, setCategories] = useState<Category[]>([])
  const [units, setUnits] = useState<Unit[]>([])

  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    sku: '',
    category_id: '',
    price: '',
    mrp: '',
    cost_price: '',
    stock_quantity: '0',
    min_stock_level: '5',
    max_stock_level: '100',
    unit: 'pcs',
    gst_rate: '18',
    gst_type: 'inclusive',
    hsn_code: '',
    is_active: true
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    loadCategories()
    loadUnits()
    if (isEditMode && id) {
      loadProduct(id)
    }
  }, [isEditMode, id])

  const loadCategories = async () => {
    try {
      const response = await apiClient.getCategories()
      const categoriesData = response.categories || response.data?.categories || []
      setCategories(categoriesData)
    } catch (error) {
      console.error('Failed to load categories:', error)
      toast.error('Failed to load categories')
    }
  }

  const loadUnits = async () => {
    try {
      const response = await apiClient.getUnits()
      const unitsData = response.units || response.data?.units || []
      setUnits(unitsData)
    } catch (error) {
      console.error('Failed to load units:', error)
      toast.error('Failed to load units')
    }
  }

  const loadProduct = async (productId: string) => {
    try {
      setLoading(true)
      const response = await apiClient.getProduct(productId)
      const product = response.product
      
      setFormData({
        name: product.name || '',
        description: product.description || '',
        sku: product.sku || '',
        category_id: product.category_id || '',
        price: product.price?.toString() || '',
        mrp: product.mrp?.toString() || '',
        cost_price: product.cost_price?.toString() || '',
        stock_quantity: product.stock_quantity?.toString() || '0',
        min_stock_level: product.min_stock_level?.toString() || '5',
        max_stock_level: product.max_stock_level?.toString() || '100',
        unit: product.unit || 'pcs',
        gst_rate: product.gst_rate?.toString() || '18',
        gst_type: (product as any).gst_type || 'inclusive',
        hsn_code: product.hsn_code || '',
        is_active: product.is_active !== false
      })
    } catch (error) {
      console.error('Failed to load product:', error)
      toast.error('Failed to load product')
      navigate('/products')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: keyof ProductFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const generateSKU = () => {
    const prefix = formData.name.substring(0, 3).toUpperCase()
    const timestamp = Date.now().toString().slice(-4)
    const sku = `${prefix}${timestamp}`
    setFormData(prev => ({ ...prev, sku }))
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required'
    }

    if (!formData.sku.trim()) {
      newErrors.sku = 'SKU is required'
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required'
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid selling price is required'
    }

    if (!formData.mrp || parseFloat(formData.mrp) <= 0) {
      newErrors.mrp = 'Valid MRP is required'
    }

    if (parseFloat(formData.mrp) < parseFloat(formData.price)) {
      newErrors.mrp = 'MRP should be greater than or equal to selling price'
    }

    if (!formData.cost_price || parseFloat(formData.cost_price) <= 0) {
      newErrors.cost_price = 'Valid cost price is required'
    }

    if (parseFloat(formData.cost_price) > parseFloat(formData.price)) {
      newErrors.cost_price = 'Cost price should be less than or equal to selling price'
    }

    if (!formData.gst_rate || parseFloat(formData.gst_rate) < 0) {
      newErrors.gst_rate = 'Valid GST rate is required'
    }

    if (!formData.hsn_code.trim()) {
      newErrors.hsn_code = 'HSN code is required for GST compliance'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      toast.error('Please fix the validation errors')
      return
    }

    setSaving(true)
    
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        mrp: parseFloat(formData.mrp),
        cost_price: parseFloat(formData.cost_price),
        stock_quantity: parseInt(formData.stock_quantity),
        min_stock_level: parseInt(formData.min_stock_level),
        max_stock_level: parseInt(formData.max_stock_level),
        gst_rate: parseFloat(formData.gst_rate),
        gst_type: formData.gst_type
      }

      if (isEditMode && id) {
        await apiClient.updateProduct(id, productData)
        toast.success('Product updated successfully!')
      } else {
        await apiClient.createProduct(productData)
        toast.success('Product created successfully!')
      }

      navigate('/products')
    } catch (error: any) {
      toast.error(error.message || `Failed to ${isEditMode ? 'update' : 'create'} product`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading product...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/products')}
          className="flex items-center gap-2 self-start sm:self-center px-2 sm:px-4"
          size="sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Back to Products</span>
        </Button>
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            {isEditMode ? 'Edit Product' : 'Add New Product'}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            {isEditMode ? 'Update product information' : 'Create a new product for your inventory'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Package className="w-4 h-4 sm:w-5 sm:h-5" />
              Basic Information
            </CardTitle>
            <CardDescription className="text-sm">
              Enter the basic details about your product
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Product Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter product name"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Category *
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => handleInputChange('category_id', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-700 ${
                    errors.category_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.category_id && (
                  <p className="text-sm text-red-600">{errors.category_id}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  SKU / Product Code *
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={generateSKU}
                    className="text-xs"
                  >
                    Generate
                  </Button>
                </label>
                <Input
                  value={formData.sku}
                  onChange={(e) => handleInputChange('sku', e.target.value.toUpperCase())}
                  placeholder="Enter SKU"
                  className={errors.sku ? 'border-red-500' : ''}
                />
                {errors.sku && (
                  <p className="text-sm text-red-600">{errors.sku}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Unit
                </label>
                <select
                  value={formData.unit}
                  onChange={(e) => handleInputChange('unit', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-700"
                >
                  <option value="">Select unit</option>
                  {units.map(unit => (
                    <option key={unit.id} value={unit.short_name}>
                      {unit.name} ({unit.short_name})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                placeholder="Enter product description"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-700"
              />
            </div>
          </CardContent>
        </Card>

        {/* Pricing Information */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5" />
              Pricing Information
            </CardTitle>
            <CardDescription className="text-sm">
              Set the pricing details for your product
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Cost Price *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cost_price}
                  onChange={(e) => handleInputChange('cost_price', e.target.value)}
                  placeholder="0.00"
                  className={errors.cost_price ? 'border-red-500' : ''}
                />
                {errors.cost_price && (
                  <p className="text-sm text-red-600">{errors.cost_price}</p>
                )}
                <p className="text-xs text-gray-500">Purchase price of the product</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Selling Price *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  placeholder="0.00"
                  className={errors.price ? 'border-red-500' : ''}
                />
                {errors.price && (
                  <p className="text-sm text-red-600">{errors.price}</p>
                )}
                <p className="text-xs text-gray-500">Price at which you sell to customers</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  MRP (Maximum Retail Price) *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.mrp}
                  onChange={(e) => handleInputChange('mrp', e.target.value)}
                  placeholder="0.00"
                  className={errors.mrp ? 'border-red-500' : ''}
                />
                {errors.mrp && (
                  <p className="text-sm text-red-600">{errors.mrp}</p>
                )}
                <p className="text-xs text-gray-500">Printed price on product packaging</p>
              </div>
            </div>

            {/* Profit Margin Display */}
            {formData.cost_price && formData.price && (
              <div className="p-3 sm:p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-green-900 dark:text-green-200">Profit Margin</p>
                    <p className="text-base sm:text-lg font-bold text-green-700 dark:text-green-400">
                      ₹{(parseFloat(formData.price) - parseFloat(formData.cost_price)).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-green-900 dark:text-green-200">Profit %</p>
                    <p className="text-base sm:text-lg font-bold text-green-700 dark:text-green-400">
                      {(((parseFloat(formData.price) - parseFloat(formData.cost_price)) / parseFloat(formData.cost_price)) * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tax & Compliance */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Percent className="w-4 h-4 sm:w-5 sm:h-5" />
              Tax & Compliance
            </CardTitle>
            <CardDescription className="text-sm">
              GST and compliance information for the product
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  GST Rate (%) *
                </label>
                <select
                  value={formData.gst_rate}
                  onChange={(e) => handleInputChange('gst_rate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-700 ${
                    errors.gst_rate ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="0">0% - Essential Items</option>
                  <option value="5">5% - Basic Necessities</option>
                  <option value="12">12% - Processed Foods</option>
                  <option value="18">18% - Most Goods & Services</option>
                  <option value="28">28% - Luxury Items</option>
                </select>
                {errors.gst_rate && (
                  <p className="text-sm text-red-600">{errors.gst_rate}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  GST Type *
                </label>
                <select
                  value={formData.gst_type}
                  onChange={(e) => handleInputChange('gst_type', e.target.value as 'inclusive' | 'exclusive')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-700"
                >
                  <option value="inclusive">Inclusive - Tax included in price</option>
                  <option value="exclusive">Exclusive - Tax added to price</option>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formData.gst_type === 'inclusive'
                    ? 'Price already includes GST. Tax will be calculated as part of the price.'
                    : 'GST will be added on top of the price during billing.'
                  }
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  HSN Code *
                </label>
                <Input
                  value={formData.hsn_code}
                  onChange={(e) => handleInputChange('hsn_code', e.target.value)}
                  placeholder="Enter HSN/SAC code"
                  className={errors.hsn_code ? 'border-red-500' : ''}
                />
                {errors.hsn_code && (
                  <p className="text-sm text-red-600">{errors.hsn_code}</p>
                )}
                <p className="text-xs text-gray-500">
                  Harmonized System of Nomenclature code for GST
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stock Management */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg">Stock Management</CardTitle>
            <CardDescription className="text-sm">
              Set stock levels and inventory alerts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Current Stock
                </label>
                <Input
                  type="number"
                  value={formData.stock_quantity}
                  onChange={(e) => handleInputChange('stock_quantity', e.target.value)}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Minimum Stock Level
                </label>
                <Input
                  type="number"
                  value={formData.min_stock_level}
                  onChange={(e) => handleInputChange('min_stock_level', e.target.value)}
                  placeholder="5"
                />
                <p className="text-xs text-gray-500">Alert when stock falls below this level</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Maximum Stock Level
                </label>
                <Input
                  type="number"
                  value={formData.max_stock_level}
                  onChange={(e) => handleInputChange('max_stock_level', e.target.value)}
                  placeholder="100"
                />
                <p className="text-xs text-gray-500">Maximum inventory capacity</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active Product
                </span>
              </label>
              <Badge variant={formData.is_active ? 'success' : 'secondary'}>
                {formData.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 pt-4 sm:pt-6 sticky bottom-0 sm:static bg-white dark:bg-gray-900 p-4 sm:p-0 -mx-4 sm:mx-0 border-t sm:border-0 border-gray-200 dark:border-gray-700">
          <LoadingButton
            type="submit"
            isLoading={saving}
            loadingText={isEditMode ? "Updating..." : "Creating..."}
            className="flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <Save className="w-4 h-4" />
            {isEditMode ? 'Update Product' : 'Create Product'}
          </LoadingButton>

          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/products')}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  )
}